import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { FileUp } from 'lucide-react';
import { useDialog } from '../../../context/Dialog';
import ImageCropperDialog from '../ImageCropperDialog';
import useIonicToast from '../../../hooks/useIonicToast';
import { FileTypeKey, FileTypeValue } from '../../../types/FileType';
import useFileDropZone from '../../../hooks/useFileDropZone';
import useNativeFilePicker from '../../../hooks/useNativeFilePicker';
import FileDropBoxImagePreview from './FileDropBoxImagePreview';

type Props = {
  className?: string;
  warning?: string;
  acceptedTypes: Partial<Record<FileTypeKey, Array<FileTypeValue>>>;
  value?: File[] | File;
  onChange?: (files: File[] | File) => void;
  maxFiles?: number;
  maxSize?: number;
  errorMessage?: string;
  allowCropping?: boolean;
  cropperAspectRatio?: number;
  disabled?: boolean;
  name?: string;
  allowPreview?: boolean;
  /**
   * URL of an already-uploaded image to show as a preview when no new file is
   * staged. Rendered via `<img>` so it works even when the host bucket lacks
   * CORS headers for `fetch()`.
   */
  existingPreviewUrl?: string;
};

/**
 * @param maxSize is the maximum number of each file in megabyte
 */
const FileDropBox = forwardRef<HTMLInputElement, Props>(
  (
    {
      className = '',
      warning,
      acceptedTypes = { 'application/pdf': ['.pdf'] },
      value = [],
      onChange,
      errorMessage,
      maxSize = 0.5,
      maxFiles = 1,
      allowCropping = false,
      cropperAspectRatio = 1,
      disabled,
      allowPreview = false,
      existingPreviewUrl,
    },
    ref // <-- forwarded ref from Controller
  ) => {
    // const [files, setFiles] = React.useState<File[]>([]);
    const { presentIonToast } = useIonicToast();
    const { openDialog, closeDialog } = useDialog();
    const valueIsArray = Array.isArray(value);
    const arrayedValue = valueIsArray ? value : value ? [value] : [];

    const showCropper = (
      image: Array<File>,
      callback: (images: Array<File>) => void
    ) => {
      openDialog(
        <ImageCropperDialog
          onFinished={(images: File[]) => {
            closeDialog({ keepParent: true });
            callback(images);
          }}
          aspectRatio={cropperAspectRatio}
          onCancel={() => closeDialog({ keepParent: true })}
          images={image}
        />,
        { allowBackgroundDismiss: false }
      );
    };

    const onDrop = React.useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length < 1) return;

        // Single-file mode: a new pick replaces the staged file rather than
        // erroring. Multi-file mode keeps the cumulative-limit guard.
        if (maxFiles === 1) {
          const callback = (croppedFiles: Array<File>) => {
            onChange?.(croppedFiles[0]);
          };
          if (allowCropping) showCropper(acceptedFiles.slice(0, 1), callback);
          else callback(acceptedFiles.slice(0, 1));
          return;
        }

        if (acceptedFiles.length + arrayedValue.length > maxFiles) {
          presentIonToast(`Maximum ${maxFiles} files allowed.`, 'error');
          return;
        }

        const callback = (croppedFiles: Array<File>) => {
          const newFiles = [...arrayedValue, ...croppedFiles];
          onChange?.(newFiles);
        };

        if (allowCropping) showCropper(acceptedFiles, callback);
        else callback(acceptedFiles);
      },
      [onChange, arrayedValue, maxFiles]
    );

    const onRemove = (fileName: string) => {
      const updatedBucket = arrayedValue.filter(
        (file) => file.name !== fileName
      );
      // setFiles(updatedBucket);
      onChange?.(maxFiles === 1 ? updatedBucket[0] : updatedBucket);
    };

    const { getRootProps, getInputProps, isDragActive } = useFileDropZone({
      maxFiles,
      acceptableType: acceptedTypes,
      files: arrayedValue,
      maxSize,
      onDrop,
      // For single-file mode the dropzone stays active when a file is staged
      // so users can drop/pick a replacement. Only multi-file mode locks it
      // when the cumulative limit is hit.
      disabled: (maxFiles > 1 && arrayedValue.length >= maxFiles) || disabled,
    });

    const { isNative, useFilePicker } = useNativeFilePicker({
      maxFiles,
      onChange: onDrop,
      acceptableType: acceptedTypes,
    });

    // SHARED CSS UTILITY
    const wrapperStyle = clsx(className, 'max-w-full space-y-2');

    const showExistingPreview =
      Boolean(existingPreviewUrl) && arrayedValue.length === 0;

    // When there's something to preview (either a staged file or an existing
    // server-side image), treat the dropzone as a "replace" affordance instead
    // of a fresh upload prompt.
    const hasAnyPreview =
      arrayedValue.length >= maxFiles || showExistingPreview;

    const existingPreviewBlock = showExistingPreview ? (
      <div className="p-4 rounded-md bg-gray-50 rounded-b-xl">
        <img
          src={existingPreviewUrl}
          alt=""
          className="w-full max-h-48 object-contain rounded"
        />
      </div>
    ) : null;

    if (isNative) {
      return (
        <div className={wrapperStyle}>
          <div
            className={`w-full flex flex-col items-center text-center gap-6 p-6 border-2 border-dashed rounded-xl border-border`}
          >
            <div className="p-4 rounded-full text-primary bg-customs-light-blue-foreground">
              <FileUp className="size-6" />
            </div>
            <div className="cursor-pointer w-full flex flex-col items-center gap-1 rounded-sm">
              <h6 className="medium-emphasis-text font-medium">
                <span className="font-bold px-1">
                  <button
                    type="button"
                    disabled={disabled}
                    className="text-primary"
                    onClick={useFilePicker}
                  >
                    <h6>Click here</h6>
                  </button>
                </span>
                <span>
                  {hasAnyPreview
                    ? 'to choose a different file'
                    : 'to upload file'}
                </span>
              </h6>
              {warning && arrayedValue.length < maxFiles && (
                <p className="low-emphasis-text font-medium">{warning}</p>
              )}
            </div>
            {errorMessage && (
              <small className="text-red-600">{errorMessage}</small>
            )}
          </div>

          {arrayedValue.length > 0 && (
            <div className="p-4 space-y-0.5 medium-emphasis-text rounded-md bg-gray-50 rounded-b-xl">
              {arrayedValue.map((file, i) => (
                <FileDropBoxImagePreview
                  key={`native-upload-${i}`}
                  aspectRatio={cropperAspectRatio}
                  allowPreview={allowPreview}
                  file={file}
                  onRemove={onRemove}
                />
              ))}
            </div>
          )}
          {existingPreviewBlock}
        </div>
      );
    }

    return (
      <div className={wrapperStyle}>
        <div
          {...getRootProps()}
          className={`w-full flex flex-col items-center text-center gap-6 p-6 border-2 border-dashed rounded-xl ${
            isDragActive ? 'border-primary' : 'border-border'
          }`}
        >
          {/* forward the ref to the real DOM input element */}
          <input ref={ref} {...getInputProps()} />

          <div className="p-4 rounded-full text-primary bg-customs-light-blue-foreground">
            <FileUp className="size-6" />
          </div>
          <div
            className={`${!disabled ? 'cursor-pointer' : ''}   w-full flex flex-col items-center gap-1 rounded-sm`}
          >
            <h6 className="medium-emphasis-text font-medium">
              <span className="font-bold px-1">
                <button
                  type="button"
                  disabled={disabled}
                  className="text-primary"
                >
                  Click here
                </button>
              </span>
              <span className={`${disabled ? 'opacity-80' : ''}`}>
                {hasAnyPreview
                  ? 'to choose a different file'
                  : 'to upload file or drag and drop'}
              </span>
            </h6>
            {warning && arrayedValue.length < maxFiles && (
              <p className="low-emphasis-text font-medium">{warning}</p>
            )}
          </div>
          {errorMessage && (
            <small className="text-red-600">{errorMessage}</small>
          )}
        </div>

        {arrayedValue.length > 0 && (
          <div className="p-4 space-y-0.5 medium-emphasis-text rounded-md bg-gray-50 rounded-b-xl">
            {arrayedValue.map((file, i) => (
              <FileDropBoxImagePreview
                key={`web-upload-${i}`}
                aspectRatio={cropperAspectRatio}
                allowPreview={allowPreview}
                file={file}
                onRemove={onRemove}
              />
            ))}
          </div>
        )}
        {existingPreviewBlock}
      </div>
    );
  }
);
const Memo = React.memo(FileDropBox);
Memo.displayName = 'FileDropBox';
export default Memo;
